/**
 * An error response's JSON body, whatever form the request asked for.
 *
 * A request made with `responseType: 'blob'` (or `'arraybuffer'`) receives
 * its refusal in that form too, so `response.data.error.code` is undefined
 * and a 401 `mfa_required` reads as any other 401 (BD-08): one useless
 * refresh-and-retry — the refreshed token carries the same `auth_time` — then
 * a sign-in without `prompt=login`, which the authorization server can answer
 * from its own session without asking for a new code. Main reads its Blob
 * refusals the same way (src/api/mfaRequired.js `isMfaRequiredBlob`).
 */

// jsdom's Blob (and older Safari's) has no text(); FileReader works everywhere.
export function readBlobText(blob) {
  if (blob && typeof blob.text === 'function') return blob.text();
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ''));
    reader.onerror = () => reject(reader.error);
    reader.readAsText(blob);
  });
}

function arrayBufferText(buffer) {
  const bytes = new Uint8Array(buffer);
  if (typeof TextDecoder !== 'undefined') return new TextDecoder().decode(bytes);
  return Array.from(bytes, (byte) => String.fromCharCode(byte)).join('');
}

/**
 * The parsed body when it is JSON in a Blob, an ArrayBuffer or a string; the
 * body itself when it is already an object; `{}` when it cannot be read — a
 * PDF error page, an empty body — which leaves the caller with the status.
 */
export async function errorBodyOf(data) {
  if (data === null || data === undefined) return {};

  try {
    if (typeof Blob !== 'undefined' && data instanceof Blob) {
      return JSON.parse(await readBlobText(data)) || {};
    }
    if (typeof ArrayBuffer !== 'undefined' && data instanceof ArrayBuffer) {
      return JSON.parse(arrayBufferText(data)) || {};
    }
    if (typeof data === 'string') {
      return data.trim() === '' ? {} : (JSON.parse(data) || {});
    }
  } catch (_) {
    return {};
  }

  return typeof data === 'object' ? data : {};
}
